import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Card, FormControl, FormLabel, styled, useTheme } from "@mui/material";

import { ReceiptLongOutlined, LoyaltyOutlined, SavingsOutlined, MailOutline, Close } from "@mui/icons-material";
import { useOutletContext } from "react-router-dom";
import toast from "react-hot-toast";
import PayPal from "./images/paypal.png";
import Venmo from "./images/venmo.png";
import FlexBox from "./components/FlexBox";
import { AccountHeader, CapHeader, ClickableText, H6, Paragraph, Tiny } from "./components/Typography";
import LIAccountDataRow from "./components/LIAccountDataRow";
import { AccountWrap, GradientTop, ModalButtonWrap, ModalBox, SecondaryButton } from "./components/pieces";
import { AccountModal, Block } from "./accountPieces";
import { Axios, buttonGradient, formatPhone, topSeparatorMargin } from "../../common/utils";
import { baseApi } from "../../common/constants";
import { fieldRules, GreenSwitch } from "../../common/commonComps";
import { AccountInputBox } from "./components/TableActions";
import { accountInputBoxCSS } from "../subComps/loginPieces";
import type { Size, User, AppContext, Config, UserSettings } from "../../types";

const InputBox = styled(AccountInputBox)(accountInputBoxCSS);

const hw = { height: `30px`, width: `30px` };
const categories: Record<string, string> = {
    paypal: `mall-payments`,
    "paypal-email": `mall-payments`,
    "venmo-phone": `mall-payments`,
    daily: `mall-email`,
    direct: `mall-email`,
    huge: `mall-email`,
};

const Heading = styled(Box)(() => ({
    fontSize: 24,
    fontWeight: 600,
    marginBottom: 16,
}));

interface PreferencesProps {
    user: User;
    context: AppContext;
    setContext: (nv: Partial<Config>) => void;
    editable: boolean;
}

const Preferences = ({ user, context, setContext, editable }: PreferencesProps) => {
    const [size] = useOutletContext<[Size]>();
    const theme = useTheme();
    const [values, setValues] = useState<Record<string, boolean | string>>({ direct: true, huge: true, daily: true });
    const [loading, setLoading] = useState(false);
    const [flippingSwitches, setFlippingSwitches] = useState<string[]>([]);
    const [toRemove, setToRemove] = useState<string | null>(null);
    const [toUpdate, setToUpdate] = useState<Record<string, boolean | string> | null>(null);
    const [editing, setEditing] = useState<Record<string, Record<string, string>>>({});
    const [errors, setErrors] = useState<Record<string, string> | string>({});
    const [editingText, setEditingText] = useState(false);
    const ahSubRef = useRef<HTMLElement>(null);
    const sectionHeaders = context?.config.sectionHeaders || {};

    useEffect(() => {
        if (toRemove) {
            if (flippingSwitches.length < 2) {
                setLoading(false);
                setFlippingSwitches([]);
            } else {
                // splice mutates the array and returns removed items; original passed that array to setLoading (always truthy)
                const removed = flippingSwitches.splice(flippingSwitches.indexOf(toRemove), 1);
                setLoading(removed.length > 0);
            }
            setToRemove(null);
        }
    }, [toRemove]);

    useEffect(() => {
        if (toUpdate) {
            // name is a global reference in this scope - cast to satisfy TS
            setToRemove(name as unknown as string);
            setValues({ ...values, ...toUpdate });
        }
    }, [toUpdate]);

    useEffect(() => {
        setValues({ ...values, ...user.settings?.[`mall-email`] });
    }, []);

    const payValues: UserSettings["mall-payments"] | undefined = user.settings?.[`mall-payments`];

    const setEdit = (v: Record<string, Record<string, string>>) => {
        setEditing(v);
    };

    const editingKey = useMemo(() => Object.keys(editing)[0], [editing]);
    const clearEditing = () => setEditing({});

    const changeSetting = (name: string, value: boolean | string) => {
        setLoading(true);

        setFlippingSwitches([...flippingSwitches, name]);
        const data = { ...editing[editingKey] };
        const f = fieldRules[editingKey]?.fields[0];
        if (f?.regex) {
            data[f.v] = (data[f.v] || ``).replace(f.regex, ``);
        }

        (editingKey ? fieldRules[editingKey].validator.validate(data) : Promise.resolve())
            .then(() =>
                Axios.put(`${baseApi}setting`, {
                    name,
                    value,
                    category: categories[name],
                }).then(() => {
                    setLoading(false);
                    setToUpdate({ [name]: value });
                    clearEditing();
                    setToRemove(name);
                })
            )
            .catch((e: { params?: { path?: string }; message?: string }) => {
                setLoading(false);
                setToRemove(name);
                if (f) {
                    const errObj = typeof errors === `object` ? errors : {};
                    setErrors(e.params?.path ? { ...errObj, [e.params.path]: e.message || `` } : e.message || ``);
                } else {
                    toast.error(`Sorry, something went wrong.`);
                }
            });
    };

    return (
        <Block size={size}>
            <AccountWrap size={size}>
                <AccountHeader size={size}>Preferences</AccountHeader>
                <Card
                    sx={{
                        borderRadius: `12px 12px 0 0`,
                        px: 3,
                        pb: 3,
                        background: theme.palette.account.background,
                    }}
                >
                    <CapHeader
                        size={size}
                        py={3}
                        m={0}
                        ref={ahSubRef}
                        contentEditable={editingText}
                        suppressContentEditableWarning
                        dangerouslySetInnerHTML={{ __html: sectionHeaders.prefSub || `PAYOUT METHOD` }}
                        onBlur={(v: React.FocusEvent<HTMLElement>) => {
                            setContext({ sectionHeaders: { ...sectionHeaders, prefSub: v.target.innerHTML } });
                            setEditingText(false);
                        }}
                        {...(editable && !editingText
                            ? {
                                  onClick: () => {
                                      setEditingText(true);
                                      setTimeout(() => {
                                          ahSubRef.current?.focus();
                                      }, 0);
                                  },
                              }
                            : {})}
                        className={editingText ? `editingText` : editable ? `edit` : ``}
                    />
                    <Box>
                        <LIAccountDataRow>
                            <FlexBox alignItems="flex-start">
                                <Box sx={{ height: `40px` }} component="img" src={PayPal} />
                                <Box ml={2}>
                                    <H6 sx={{ marginBottom: `0 !important` }} m={0}>
                                        PayPal
                                    </H6>
                                    <Tiny
                                        sx={{
                                            display: `inline-block`,
                                            mr: `27px`,
                                            mt: `5px`,
                                            verticalAlign: `middle`,
                                        }}
                                        fontWeight={400}
                                        m={0}
                                    >
                                        {values[`paypal-email`] || payValues?.[`paypal-email`] || user.email}
                                    </Tiny>
                                    <Button
                                        onClick={() =>
                                            setEdit({
                                                paypal: {
                                                    [`paypal-email`]:
                                                        (values?.[`paypal-email`] as string) ||
                                                        payValues?.[`paypal-email`] ||
                                                        user.email ||
                                                        ``,
                                                },
                                            })
                                        }
                                        size="small"
                                        sx={{
                                            display: size === `small` ? `block` : `inline-block`,
                                            color: theme.palette.account.accent,
                                            minWidth: `unset`,
                                            marginTop: `5px`,
                                            verticalAlign: `middle`,
                                            pointerEvents: `all`,
                                        }}
                                    >
                                        Edit
                                    </Button>
                                </Box>
                            </FlexBox>
                            <Box>
                                <GreenSwitch
                                    disabled={
                                        (loading && flippingSwitches.includes(`paypal`)) ||
                                        (!payValues?.[`venmo-phone`] && !values[`venmo-phone`])
                                    }
                                    checked={(values?.paypal ?? payValues?.paypal) !== false}
                                    onChange={(v: React.ChangeEvent<HTMLInputElement>) =>
                                        changeSetting(`paypal`, v.target.checked)
                                    }
                                    sx={{ pointerEvents: `all` }}
                                />
                            </Box>
                        </LIAccountDataRow>
                        <LIAccountDataRow>
                            <FlexBox alignItems="flex-start">
                                <Box sx={{ height: `40px` }} component="img" src={Venmo} />
                                <Box ml={2}>
                                    <H6 sx={{ marginBottom: `0 !important` }} m={0}>
                                        Venmo
                                    </H6>
                                    <Tiny
                                        sx={{
                                            display: `inline-block`,
                                            mr: `27px`,
                                            mt: `5px`,
                                            verticalAlign: `middle`,
                                            pointerEvents: `all`,
                                        }}
                                        fontWeight={400}
                                        mt={0.3}
                                        m={0}
                                    >
                                        {values[`venmo-phone`] || payValues?.[`venmo-phone`]
                                            ? formatPhone(
                                                  (values[`venmo-phone`] || payValues?.[`venmo-phone`] || ``) as string
                                              )
                                            : `Not set up yet`}
                                    </Tiny>
                                    {payValues?.[`venmo-phone`] && (
                                        <Button
                                            onClick={() =>
                                                setEdit({
                                                    venmo: {
                                                        [`venmo-phone`]:
                                                            (values?.[`venmo-phone`] as string) ||
                                                            payValues?.[`venmo-phone`] ||
                                                            ``,
                                                    },
                                                })
                                            }
                                            size="small"
                                            sx={{
                                                display: size === `small` ? `block` : `inline-block`,
                                                color: theme.palette.account.accent,
                                                minWidth: `unset`,
                                                marginTop: `5px`,
                                                verticalAlign: `middle`,
                                                pointerEvents: `all`,
                                            }}
                                        >
                                            Edit
                                        </Button>
                                    )}
                                </Box>
                            </FlexBox>
                            <Box>
                                {values[`venmo-phone`] || payValues?.[`venmo-phone`] ? (
                                    <GreenSwitch
                                        disabled={loading && flippingSwitches.includes(`paypal`)}
                                        checked={(values?.paypal ?? payValues?.paypal) === false}
                                        onChange={(v: React.ChangeEvent<HTMLInputElement>) =>
                                            changeSetting(`paypal`, !v.target.checked)
                                        }
                                        sx={{ pointerEvents: `all` }}
                                    />
                                ) : (
                                    <Button
                                        disabled={loading}
                                        size="small"
                                        variant="contained"
                                        onClick={() => {
                                            setEdit({ venmo: {} });
                                        }}
                                        sx={{
                                            background: buttonGradient(theme),
                                            padding: `4px 8px`,
                                            minWidth: `unset`,
                                            borderRadius: 2,
                                            marginTop: 1,
                                            pointerEvents: `all`,
                                        }}
                                    >
                                        Set Up
                                    </Button>
                                )}
                            </Box>
                        </LIAccountDataRow>
                    </Box>
                </Card>
                <Card
                    sx={{
                        borderRadius: `0 0 12px 12px`,
                        px: 3,
                        pb: 3,
                        background: theme.palette.account.background,
                        mt: topSeparatorMargin(size),
                    }}
                >
                    <CapHeader size={size} py={3} m={0}>
                        COMMUNICATION
                    </CapHeader>

                    <Box>
                        <LIAccountDataRow>
                            <FlexBox alignItems="center">
                                <ReceiptLongOutlined sx={{ color: theme.palette.account.primary, ...hw }} />
                                <Box ml={2}>
                                    <H6 m={0}>Shopping Activity Email</H6>
                                    <Tiny fontWeight={400} mt={0.3} m={0}>
                                        Get notifications about the cash back you earned.
                                    </Tiny>
                                </Box>
                            </FlexBox>
                            <ClickableText sx={{ color: `#333` }}>Required</ClickableText>
                        </LIAccountDataRow>
                        <LIAccountDataRow>
                            <FlexBox alignItems="center">
                                <LoyaltyOutlined sx={{ color: theme.palette.account.primary, ...hw }} />
                                <Box ml={2}>
                                    <H6 m={0}>Daily Deals Email</H6>
                                    <Tiny fontWeight={400} mt={0.3} m={0}>
                                        Get a list of the web&apos;s best deals every day.
                                    </Tiny>
                                </Box>
                            </FlexBox>
                            <GreenSwitch
                                disabled={loading && flippingSwitches.includes(`daily`)}
                                checked={!!values.daily}
                                onChange={(v: React.ChangeEvent<HTMLInputElement>) =>
                                    changeSetting(`daily`, v.target.checked)
                                }
                            />
                        </LIAccountDataRow>
                        <LIAccountDataRow>
                            <FlexBox alignItems="center">
                                <SavingsOutlined sx={{ color: theme.palette.account.primary, ...hw }} />
                                <Box ml={2}>
                                    <H6 m={0}>Huge Cash Back Emails</H6>
                                    <Tiny fontWeight={400} mt={0.3} m={0}>
                                        Get notified about the biggest cash back events throughout the year.
                                    </Tiny>
                                </Box>
                            </FlexBox>
                            <GreenSwitch
                                disabled={loading && flippingSwitches.includes(`huge`)}
                                checked={!!values.huge}
                                onChange={(v: React.ChangeEvent<HTMLInputElement>) =>
                                    changeSetting(`huge`, v.target.checked)
                                }
                            />
                        </LIAccountDataRow>
                        <LIAccountDataRow>
                            <FlexBox alignItems="center">
                                <MailOutline sx={{ color: theme.palette.account.primary, ...hw }} />
                                <Box ml={2}>
                                    <H6 m={0}>Direct Mail Offers</H6>
                                    <Tiny fontWeight={400} mt={0.3} m={0}>
                                        Some of the biggest cash back offers are mailed to you.
                                    </Tiny>
                                </Box>
                            </FlexBox>
                            <GreenSwitch
                                disabled={loading && flippingSwitches.includes(`direct`)}
                                checked={!!values.direct}
                                onChange={(v: React.ChangeEvent<HTMLInputElement>) =>
                                    changeSetting(`direct`, v.target.checked)
                                }
                            />
                        </LIAccountDataRow>
                    </Box>
                </Card>
                {!!editingKey && (
                    <AccountModal onClose={clearEditing} size={size} open={!!editingKey}>
                        <Card sx={{ boxSizing: `border-box` }} className="modalContent">
                            <Close
                                sx={{
                                    position: `absolute`,
                                    top: 24,
                                    right: 10,
                                    zIndex: 30,
                                    cursor: `pointer`,
                                    opacity: 0.55,
                                    height: 25,
                                    width: 25,
                                    zindex: 30,
                                }}
                                onClick={() => setEdit({})}
                            />
                            {size !== `small` && <GradientTop />}
                            <ModalBox>
                                {size === `small` && (
                                    <Box p={2} sx={{ textAlign: `center` }}>
                                        <Box
                                            component="img"
                                            sx={{ width: 150 }}
                                            src={context?.adminConfig?.mall?.Color_Desktop_Logo}
                                        />
                                    </Box>
                                )}

                                <Heading>{fieldRules[editingKey].title}</Heading>

                                {fieldRules[editingKey].fields.map(e => (
                                    <FormControl
                                        key={e.l}
                                        sx={{
                                            width: `100%`,
                                            mr: e.mr,
                                            maxWidth: e.width,
                                            "& .MuiFormLabel-root": {
                                                color: `#666`,
                                            },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: `flex`,
                                                justifyContent: `space-between`,
                                                ...(e.hidden ? { display: `none` } : {}),
                                            }}
                                        >
                                            <FormLabel sx={{ fontSize: 16, fontWeight: 600 }} htmlFor={e.l}>
                                                {e.l}
                                            </FormLabel>
                                        </Box>

                                        <InputBox
                                            name={e.l}
                                            type={e.t}
                                            autoComplete={e.ac}
                                            id={e.l}
                                            disabled={loading || !!e.disabled}
                                            onClick={() =>
                                                setEdit({
                                                    [editingKey]: { ...editing[editingKey], [e.v]: `` },
                                                })
                                            }
                                            fullWidth
                                            sx={{
                                                mb: 3,
                                                marginTop: `5px`,
                                                maxWidth: `unset`,
                                                ...(e.hidden ? { display: `none` } : {}),
                                                input: { fontWeight: 500 },
                                            }}
                                            currentValue={editing[editingKey]?.[e.v] || ``}
                                            helperText={
                                                typeof errors === `object`
                                                    ? (errors as Record<string, string>)[e.v]
                                                    : undefined
                                            }
                                            error={
                                                typeof errors === `object`
                                                    ? !!(errors as Record<string, string>)[e.v]
                                                    : false
                                            }
                                            onChange={v => {
                                                setEdit({
                                                    [editingKey]: {
                                                        ...editing[editingKey],
                                                        [e.v]: e.visualFilter
                                                            ? e.visualFilter(v.target.value)
                                                            : v.target.value,
                                                    },
                                                });
                                                setErrors({});
                                            }}
                                        />

                                        {fieldRules[editingKey].text && (
                                            <Paragraph
                                                sx={{
                                                    my: 0,
                                                    color: theme.palette.account.accent,
                                                    fontSize: 16,
                                                }}
                                            >
                                                {fieldRules[editingKey].text}
                                            </Paragraph>
                                        )}
                                    </FormControl>
                                ))}
                                <ModalButtonWrap>
                                    <SecondaryButton
                                        disabled={loading}
                                        onClick={() => {
                                            setEdit({});
                                        }}
                                    >
                                        Cancel
                                    </SecondaryButton>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        disabled={loading}
                                        onClick={() =>
                                            changeSetting(
                                                fieldRules[editingKey].fields[0].v,
                                                editing[editingKey][fieldRules[editingKey].fields[0].v]
                                            )
                                        }
                                        sx={{
                                            background: buttonGradient(theme),
                                            padding: `4px 8px`,
                                            minWidth: `unset`,
                                            borderRadius: 2,
                                            "&.Mui-disabled": {
                                                background: `#999 !important`,
                                                opacity: 0.5,
                                            },
                                        }}
                                    >
                                        {fieldRules[editingKey].btnText || `Save`}
                                    </Button>
                                </ModalButtonWrap>
                            </ModalBox>
                        </Card>
                    </AccountModal>
                )}
            </AccountWrap>
        </Block>
    );
};

export default Preferences;
